import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Column,
  Row,
  Grid,
  Spacing,
  Text,
  TextLink,
  withTheme
} from '@dls/web';
import { PlanSelector, FlowStickyFooter } from '@singtel-web/widgets';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import SEO from '../SEO';
import LoadingOverlay from '../LoadingOverlay';
import PageWrapper from './page-wrapper';
import NeedHelpSection from './NeedHelpSection';
import Footer from '../Footer/Footer';
import {
  api,
  btlActions,
  orderActions,
  planActions,
  userActions
} from '@detox/actions';
import { navigation } from '../../middlewares/navigation-constants';
import {
  BTLOfferDetails,
  NormalizedBTLOffer,
  RootState
} from '../../types/btloffer.types';
import useTranslate from '../../hooks/useTranslation';
import {
  flattenNodes,
  formatServiceNumberForPxAddons,
  getBTLAddonCategoryList,
  getBTLLayoutModel,
  getBTLOfferAddons,
  getPayloadForCalculatePayable,
  handleByPassPXAddon,
  mergeBTLAddonsWithPricing
} from '../../helpers/common';
import { ProductDetailsWrapper } from '../../templates/phone/styles';
import { APP_TYPE_ANY } from '../../types/common.types';
import BTLAddonsCarousel from './BTLAddonsCarousel';
import {
  filterMainline,
  getPendingOrderParams
} from '../../helpers/chooseYourNumberHelper';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { ACTION_TYPES } from '../../constants/actions';
import { BTL_CONFIGS } from '../../constants/common';
import { btlTncLink } from '../../config/links';
import {
  buildBTLOrderPayload,
  getStickyFooterLiner,
  isDynamicBoidOffer,
  normalizeBTLOffer
} from '../../helpers/btl-offer-normalizer';

const { eShopGetPxAddons, eShopPxAddonsCalculatePayable } = api.onlineApi;
const { isMobileService } = api.mcss.helpers;

const StyledPlanSelector = withTheme(styled.div`
  background-color: '#f5f5f8';
`);

const BTLOfferDetailPage = props => {
  const {
    data,
    abTestingLineSelectionEnabled,
    abTestPxAddonsFeatureEnabled,
    abTestApiPending,
    abTestApiSuccess
  } = props;
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const {
    userInformation,
    order,
    promotions,
    plan,
    product,
    user,
    addons
  } = useSelector((state: RootState) => ({
    userInformation: state.user?.information,
    order: state.order,
    promotions: state.promotions,
    plan: state.plan,
    product: state.product,
    user: state.user,
    addons: state.addons
  }));
  const { selectedPlan } = plan;
  const [btlOfferDetail, setBTLOfferDetails] = useState<BTLOfferDetails>();
  // Works for both static and dynamic response shapes
  const [
    normalizedOffer,
    setNormalizedOffer
  ] = useState<NormalizedBTLOffer | null>(null);

  const isLoggedIn = !!userInformation?.clientContext;
  const loadingOrder =
    order?.loading ||
    promotions?.btlOfferLoading ||
    addons?.isSkeletonLoading ||
    user?.loading;

  const { allPlan, allAddon, allAddonGroup, allAddonPreTick } = data;
  const plans = useMemo(() => flattenNodes(allPlan), [allPlan]);
  const addonList = useMemo(() => flattenNodes(allAddon), [allAddon]);

  const addonGroupInfoLookup = useMemo(
    () =>
      allAddonGroup.edges.reduce((accum, cur) => {
        accum[cur.node.displayName] = cur.node;
        return accum;
      }, {}),
    [allAddonGroup.edges]
  );

  const allAddonPreTickList = useMemo(
    () => (data ? flattenNodes(allAddonPreTick) : []),
    [allAddonPreTick, data]
  );

  const pretickBoIds = allAddonPreTickList
    .flatMap(addon => addon.boIds || [])
    .filter(boid => BTL_CONFIGS.CAMPIGN_ADDON_ID_LIST.includes(boid));

  const filteredBTLAddonsWithPretick = useMemo(() => {
    return addonList
      .filter(addon =>
        BTL_CONFIGS.CAMPIGN_ADDON_ID_LIST.includes(
          addon.productSpecContainmentID
        )
      )
      .map(addon => ({
        ...addon,
        isPretick: pretickBoIds.includes(addon.productSpecContainmentID)
      }));
  }, [addonList, pretickBoIds]);

  // serviceAddonList declared as useMemo BEFORE all useEffects so it is available when BTL_ADDON_UPDATE is dispatched
  const serviceAddonList = useMemo(
    () => getBTLOfferAddons(filteredBTLAddonsWithPretick, addonGroupInfoLookup),
    [filteredBTLAddonsWithPretick, addonGroupInfoLookup]
  );

  useEffect(() => {
    dispatch({
      type: ACTION_TYPES.SETTINGS.SET_APP_CONFIG,
      payload: { skipAddons: true }
    });
    dispatch({
      type: ACTION_TYPES.SIMTYPE.SET_SIMTYPE,
      payload: 'noSIM'
    });
  }, []);

  // Fetch BTL offer once logged in and campaign ID is confirmed
  useEffect(() => {
    if (isLoggedIn && promotions?.btlCampignId) {
      dispatch(
        btlActions.getBtlOffersWithEshop({
          btlCampaignId: promotions?.btlCampignId
        })
      );
    }
  }, [promotions?.btlCampignId, isLoggedIn]);

  // Process BTL offer response - normalizeBTLOffer handles both static and dynamic shapes transparently
  useEffect(() => {
    if (!promotions?.btlOfferData) return;
    if (promotions?.btlOfferData?.customerDetails?.eligible) {
      setBTLOfferDetails(promotions?.btlOfferData);

      // Normalize once — all downstream logic uses normalizedOffer
      const normalized = normalizeBTLOffer(promotions?.btlOfferData);
      setNormalizedOffer(normalized);

      // Match AEM Plan using normalized planBoid - works for both static and dynalic cases
      const matchedPlan = plans.find(
        plan => plan.basePriceSchemaId === normalized?.planBoid?.toString()
      );

      if (normalized?.planBoid) {
        const isDynamic = isDynamicBoidOffer(promotions?.btlOfferData);
        if (isDynamic) {
          // pxVas items are passed separately for PX addon fetching
          dispatch({
            type: ACTION_TYPES.ADDONS.BTL_ADDON_UPDATE,
            payload: {
              btlPxAddons: normalized.pxVasList,
              btlNonPxAddons: normalized.vasBoidList,
              btlVasTechBoidOffers: normalized.vasTechBOIDList,
              addonData: serviceAddonList
            }
          });
        } else {
          // Static : existing getBTLAddonCategoryList logic preserved
          const btlAddonCategoryInfo = getBTLAddonCategoryList(
            normalized.rawVas
          );
          const nonPxBoidList = btlAddonCategoryInfo?.uniqueNonPXAddonList.flatMap(
            vas => [vas.BOID, vas.discountBOID].filter(Boolean)
          );

          dispatch({
            type: ACTION_TYPES.ADDONS.BTL_ADDON_UPDATE,
            payload: {
              btlPxAddons: btlAddonCategoryInfo?.uniquePxAddonList,
              btlNonPxAddons: nonPxBoidList,
              addonData: serviceAddonList
            }
          });
        }
      }

      if (matchedPlan) {
        dispatch(planActions.setSelectedPlan(matchedPlan));
      }
    } else {
      // to handle ineligible customer case or error case
      navigate(`/${navigation.BTL_ERROR_PAGE}`, { replace: true });
    }
  }, [promotions?.btlOfferData]);

  // Handle BTL Offer API errors
  useEffect(() => {
    if (promotions?.btlOfferError) {
      // if you receive eshop error while getting btl offer details, navigate to error page
      navigate(`/${navigation.ERROR}`, {
        replace: true
      });
    } else if (
      promotions?.btlCampignErrorDetail &&
      promotions?.btlCampignErrorDetail?.error
    ) {
      navigate(`/${navigation.BTL_ERROR_PAGE}`, { replace: true });
    }
  }, [promotions?.btlOfferError, promotions?.btlCampignErrorDetail?.error]);

  /** Fetch PX addons after AB test resolves
   *  For dynamic offers: pxVasList from normalizedOffer is used as btlPxAddons
   *  For static offers: btlPxAddons comes from getBTLAddonCategoryList
   *  Both cases flow through the same PX addon fetching logic below
   */
  useEffect(() => {
    const isEligible = promotions?.btlOfferData?.customerDetails?.eligible;
    const pxAddonsEnabled =
      !abTestApiPending && abTestApiSuccess && abTestPxAddonsFeatureEnabled;
    const hasPxAddons = addons?.btlAddon?.btlPxAddons?.length > 0;

    if (!isEligible || !pxAddonsEnabled || !hasPxAddons) return;

    const resetPxAddons = () => {
      dispatch({
        type: ACTION_TYPES.ADDONS.PX_ADDONS_RESET,
        payload: {
          pxAddons: null,
          pxExistingAddons: null,
          productOrderId: null
        }
      });
    };

    const getPxAddonsApi = async () => {
      try {
        dispatch({
          type: ACTION_TYPES.ADDONS.ADDONS_FETCH_LOADING
        });
        const serviceNumber = formatServiceNumberForPxAddons(
          promotions?.btlOfferData?.customerDetails?.serviceLines[0]
        );

        const pxAddons = await eShopGetPxAddons(serviceNumber);
        if (handleByPassPXAddon(pxAddons)) {
          resetPxAddons();
          return;
        }

        const { filterAddon, requestPayload } = getPayloadForCalculatePayable(
          pxAddons?.allAddons,
          pxAddons?.existingAddons,
          addons?.btlAddon?.btlPxAddons
        );

        const calculatePayableResponse = await eShopPxAddonsCalculatePayable({
          serviceNumber,
          addonsList: requestPayload
        });

        if (handleByPassPXAddon(calculatePayableResponse)) {
          resetPxAddons();
          return;
        }
        const mergedAddons = mergeBTLAddonsWithPricing(
          filterAddon,
          calculatePayableResponse?.body?.data
        );

        dispatch({
          type: ACTION_TYPES.ADDONS.PX_ADDONS_FETCH_SUCCESS,
          payload: {
            pxAddons: mergedAddons,
            pxExistingAddons: pxAddons.existingAddons
          }
        });
      } catch (error) {
        resetPxAddons();
      }
    };

    getPxAddonsApi();
  }, [abTestApiPending, abTestApiSuccess, addons?.btlAddon?.btlPxAddons]);

  // Fetch user products after plan is fetched from access Api
  useEffect(() => {
    if (
      selectedPlan?.planId &&
      promotions?.btlOfferData?.customerDetails?.eligible
    ) {
      const options = {
        getMainLinesOnly: false,
        shouldFetchMainLines: isSimOnlyPlan(selectedPlan),
        isEshopLineSelection: abTestingLineSelectionEnabled
      };
      dispatch(
        userActions.getUserInfoAndProducts(
          abTestingLineSelectionEnabled
            ? filterMainline(plans)
            : isMobileService,
          options
        )
      );
    }
  }, [selectedPlan?.planId]);

  useEffect(() => {
    if (order?.hasProcessingOrder) {
      navigate(
        `/${navigation.INFO}${getPendingOrderParams(
          !!user.information?.userDetails?.loginId
        )}`
      );
      dispatch({
        type: ACTION_TYPES.ORDER.RESET_PROCESSING_ORDER
      });
    }
  }, [order?.hasProcessingOrder]);

  // Compute sticky cart content with normalizedOffer
  const stickyCartContent = useMemo(() => {
    return getStickyFooterLiner(normalizedOffer) || [];
  }, [normalizedOffer]);

  const proceedToShoppingCart = () => {
    const createBTLOrderToProceed = async () => {
      try {
        const service = user?.products?.find(
          p =>
            p?.serviceId ===
            promotions?.btlOfferData?.customerDetails?.serviceLines?.[0]
        );

        const payload = buildBTLOrderPayload({
          selectedPlan,
          service,
          user,
          product,
          addons,
          promotions,
          normalizedOffer,
          addonList,
          allAddonPreTickList
        });

        await (dispatch(orderActions.createBTLOrder(payload)) as APP_TYPE_ANY);

        navigate(`/${navigation.SHOPPING_CART_PAGE}`);
      } catch (e) {
        console.error('Error creating BTL Recontract Order:', e);
      }
    };

    createBTLOrderToProceed();
  };

  const handlePlanSelect = plan => {
    dispatch(planActions.setSelectedPlan(plan));
  };

  const planSelectorArgs = {
    selectedPlan: getBTLLayoutModel(selectedPlan, btlOfferDetail)[0],
    defaultImage: {
      src: normalizedOffer?.displayItems?.imageURL ?? '',
      alt: 'Plan image'
    },
    planSelectorOptions: [getBTLLayoutModel(selectedPlan, btlOfferDetail)[0]],
    hideSelector: true,
    onPlanSelect: handlePlanSelect
  };

  return (
    <>
      <SEO title="BTL Offer Page" />
      {loadingOrder && <LoadingOverlay />}
      {isLoggedIn && (
        <>
          <ProductDetailsWrapper>
            {loadingOrder && <LoadingOverlay />}

            {btlOfferDetail && (
              <>
                <Grid>
                  <Row noGutter>
                    <Column noGutter>
                      <Spacing top={3} bottom={3}>
                        <Text type="header" tag="h4">
                          {t('BTL_MOBILE_SERVICE', [
                            btlOfferDetail?.customerDetails?.serviceLines[0]?.toString()
                          ])}
                        </Text>
                      </Spacing>
                    </Column>
                  </Row>
                </Grid>
                <StyledPlanSelector>
                  <Row>
                    <Column>
                      <Spacing bottom={3}>
                        <PlanSelector.Layout1 {...planSelectorArgs} />
                      </Spacing>
                    </Column>
                  </Row>
                </StyledPlanSelector>
              </>
            )}

            <PageWrapper>
              <Spacing top={3} bottom={2}>
                <TextLink
                  inline
                  onClick={() => {
                    window.open(btlTncLink, '_blank');
                  }}
                  data-testid="btl-tnc-link-model"
                >
                  {t('VIEW_TNC') as APP_TYPE_ANY}
                </TextLink>{' '}
              </Spacing>
              <Spacing top={3} bottom={2}>
                <BTLAddonsCarousel serviceAddons={serviceAddonList} />
              </Spacing>
              <NeedHelpSection />
            </PageWrapper>
          </ProductDetailsWrapper>

          <Footer />
          {btlOfferDetail && (
            <FlowStickyFooter
              buttonText={t('ACTION_TEXT_PROCEED')}
              action={{
                text: t('ACTION_TEXT_PROCEED'),
                onClick: proceedToShoppingCart
              }}
              content={stickyCartContent}
            />
          )}
        </>
      )}
    </>
  );
};

export default BTLOfferDetailPage;
